/**
 * Página de Login con Azure Active Directory
 * 
 * Muestra una interfaz de login moderna con opciones para
 * autenticarse mediante Azure AD (Microsoft Entra ID)
 */

import React, { useState } from 'react';
import { useAuth } from './AuthContext';

interface LoginPageProps {
  logoUrl?: string;
  appName?: string;
  companyName?: string;
}

export const LoginPage: React.FC<LoginPageProps> = ({
  logoUrl,
  appName = 'Backlog Generator',
  companyName = 'Tu Organización',
}) => {
  const { login, loginRedirect, isLoading, error } = useAuth();
  const [useRedirect, setUseRedirect] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleLogin = async () => {
    setLocalError(null);
    try {
      if (useRedirect) {
        await loginRedirect();
      } else {
        await login();
      }
    } catch (err: any) {
      setLocalError(err.message || 'Error al iniciar sesión');
    }
  };

  const displayError = error || localError;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4">
      {/* Fondo con patrón */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAzMHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-40"></div>
      
      <div className="relative w-full max-w-md">
        {/* Card de Login */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header con gradiente */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-10 text-center">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt={appName} 
                className="h-16 mx-auto mb-4"
              />
            ) : (
              <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg 
                  className="w-12 h-12 text-white" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" 
                  />
                </svg>
              </div>
            )}
            <h1 className="text-2xl font-bold text-white mb-2">{appName}</h1>
            <p className="text-blue-100 text-sm">{companyName}</p>
          </div>

          {/* Body */}
          <div className="px-8 py-8">
            {/* Mensaje de bienvenida */}
            <div className="text-center mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Bienvenido
              </h2>
              <p className="text-gray-600 text-sm">
                Inicia sesión con tu cuenta organizacional para acceder al sistema
              </p>
            </div>

            {/* Error message */}
            {displayError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <svg 
                  className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                  />
                </svg>
                <span className="text-sm text-red-700">{displayError}</span>
              </div>
            )}

            {/* Botón de Login con Microsoft */}
            <button
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full flex items-center justify-center px-6 py-4 border border-gray-300 rounded-xl shadow-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              ) : (
                <>
                  {/* Logo de Microsoft */}
                  <svg 
                    className="w-6 h-6 mr-3" 
                    viewBox="0 0 23 23"
                  >
                    <path fill="#f35325" d="M1 1h10v10H1z"/>
                    <path fill="#81bc06" d="M12 1h10v10H12z"/>
                    <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                    <path fill="#ffba08" d="M12 12h10v10H12z"/>
                  </svg>
                  <span className="text-gray-700 font-medium group-hover:text-gray-900">
                    Iniciar sesión con Microsoft
                  </span>
                </>
              )}
            </button>

            {/* Opciones adicionales */}
            <div className="mt-6">
              <label className="flex items-center justify-center text-sm text-gray-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useRedirect}
                  onChange={(e) => setUseRedirect(e.target.checked)}
                  className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Usar redirección en lugar de popup
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-4 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
              <svg 
                className="w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                />
              </svg>
              <span>Protegido con Azure Active Directory</span>
            </div>
          </div>
        </div>

        {/* Información adicional */}
        <div className="mt-6 text-center">
          <p className="text-blue-200 text-xs">
            ¿Problemas para acceder? Contacta al administrador de tu organización
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
